import { z } from 'zod';

/**
 * Portable service-plan policy values (V2-G7, ADR-0043).
 *
 * These values deliberately do not live in `PlanLimits`. Count/megabyte
 * limits answer "how much may be provisioned"; lifecycle, billing-period
 * transfer and soft thresholds need dates, calendar durations, time zones,
 * grace periods and explicit enforcement actions.
 */

export const POLICY_ENFORCEMENT_ACTIONS = [
  'notify',
  'block_deployments',
  'suspend',
] as const;
export type PolicyEnforcementAction = (typeof POLICY_ENFORCEMENT_ACTIONS)[number];
export const policyEnforcementActionSchema = z.enum(POLICY_ENFORCEMENT_ACTIONS);

export const POLICY_ENFORCEMENT_LABELS: Record<
  PolicyEnforcementAction,
  { label: string; description: string }
> = {
  notify: {
    label: 'Notify only',
    description: 'Record the incident and notify staff; do not block or stop the subscription.',
  },
  block_deployments: {
    label: 'Block new deployments',
    description:
      'Keep running workloads online, but block new resource creation and deployment triggers until recovery or an operator extension.',
  },
  suspend: {
    label: 'Suspend subscription',
    description:
      'After the grace period, suspend the subscription through the durable lifecycle event workflow. Reactivation is an explicit operator action.',
  },
};

export const calendarDurationSchema = z.object({
  value: z.number().int().min(1).max(10_000),
  unit: z.enum(['days', 'months', 'years']),
});
export type CalendarDuration = z.infer<typeof calendarDurationSchema>;

const dateOnlySchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
  .refine((value) => {
    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(Date.UTC(year!, month! - 1, day!));
    return (
      date.getUTCFullYear() === year &&
      date.getUTCMonth() === month! - 1 &&
      date.getUTCDate() === day
    );
  }, 'Use a real calendar date');

export const timeZoneSchema = z.string().min(1).max(100).refine((value) => {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value }).format(new Date(0));
    return true;
  } catch {
    return false;
  }
}, 'Use an IANA time zone such as America/Chicago');

const byteValueSchema = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER).nullable();
const graceHoursSchema = z.number().int().min(0).max(24 * 365);

/**
 * A plan normally stores a calendar duration. An imported/customized
 * subscription stores an exact date instead. `unlimited` is Plesk `-1`.
 */
export const expiryPolicyValueSchema = z
  .object({
    kind: z.literal('expiry'),
    mode: z.enum(['unlimited', 'duration', 'date']),
    duration: calendarDurationSchema.nullable().default(null),
    expiresOn: dateOnlySchema.nullable().default(null),
    /** Warning lead time for duration-based plans. */
    warningDays: z.number().int().min(0).max(3650).nullable().default(14),
    /** Exact imported warning date, when Plesk supplied one. */
    warningOn: dateOnlySchema.nullable().default(null),
    timeZone: timeZoneSchema.default('UTC'),
    graceHours: graceHoursSchema.default(72),
    enforcement: policyEnforcementActionSchema.default('notify'),
  })
  .superRefine((value, ctx) => {
    if (value.mode === 'duration' && value.duration === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['duration'],
        message: 'A duration policy requires a calendar duration',
      });
    }
    if (value.mode === 'date' && value.expiresOn === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['expiresOn'],
        message: 'A date policy requires an expiry date',
      });
    }
    if (value.mode !== 'duration' && value.duration !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['duration'],
        message: 'Duration is only valid in duration mode',
      });
    }
    if (value.mode !== 'date' && value.expiresOn !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['expiresOn'],
        message: 'An exact expiry date is only valid in date mode',
      });
    }
  });
export type ExpiryPolicyValue = z.infer<typeof expiryPolicyValueSchema>;

/** Monthly transfer allowance plus its earlier, notification-oriented threshold. */
export const monthlyTransferPolicyValueSchema = z
  .object({
    kind: z.literal('monthly-transfer'),
    allowanceBytes: byteValueSchema.default(null),
    softThresholdBytes: byteValueSchema.default(null),
    billingAnchorDay: z.number().int().min(1).max(28).default(1),
    timeZone: timeZoneSchema.default('UTC'),
    graceHours: graceHoursSchema.default(24),
    enforcement: policyEnforcementActionSchema.default('notify'),
  })
  .superRefine((value, ctx) => {
    if (
      value.allowanceBytes !== null &&
      value.softThresholdBytes !== null &&
      value.softThresholdBytes > value.allowanceBytes
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['softThresholdBytes'],
        message: 'The soft threshold cannot exceed the monthly allowance',
      });
    }
  });
export type MonthlyTransferPolicyValue = z.infer<typeof monthlyTransferPolicyValueSchema>;

/** A notification-oriented disk threshold, kept in bytes for exact Plesk import. */
export const diskSoftPolicyValueSchema = z.object({
  kind: z.literal('disk-soft-threshold'),
  thresholdBytes: byteValueSchema.default(null),
  graceHours: graceHoursSchema.default(24),
  enforcement: policyEnforcementActionSchema.default('notify'),
});
export type DiskSoftPolicyValue = z.infer<typeof diskSoftPolicyValueSchema>;

export const servicePolicyValueSchema = z.union([
  expiryPolicyValueSchema,
  monthlyTransferPolicyValueSchema,
  diskSoftPolicyValueSchema,
]);
export type ServicePolicyValue = z.infer<typeof servicePolicyValueSchema>;

/**
 * Open records preserve values contributed by a temporarily disabled or
 * removed module, matching the limit/feature descriptor contract.
 */
export const planPoliciesSchema = z.record(servicePolicyValueSchema);
export type PlanPolicies = z.infer<typeof planPoliciesSchema>;
export const policyOverridesSchema = z.record(servicePolicyValueSchema);
export type PolicyOverrides = z.infer<typeof policyOverridesSchema>;

export const PLAN_POLICY_KINDS = [
  'expiry',
  'monthly-transfer',
  'disk-soft-threshold',
] as const;
export type PlanPolicyKind = (typeof PLAN_POLICY_KINDS)[number];

export interface PlanPolicyDescriptor {
  key: string;
  label: string;
  kind: PlanPolicyKind;
  default: ServicePolicyValue;
  group: string;
  hint?: string;
}

export const EXPIRY_POLICY_KEY = 'subscription_expiry';
export const MONTHLY_TRANSFER_POLICY_KEY = 'monthly_transfer';
export const DISK_SOFT_POLICY_KEY = 'disk_soft_threshold';

export const CORE_POLICY_DESCRIPTORS: readonly PlanPolicyDescriptor[] = [
  {
    key: EXPIRY_POLICY_KEY,
    label: 'Subscription expiry',
    kind: 'expiry',
    default: {
      kind: 'expiry',
      mode: 'unlimited',
      duration: null,
      expiresOn: null,
      warningDays: 14,
      warningOn: null,
      timeZone: 'UTC',
      graceHours: 72,
      enforcement: 'notify',
    },
    group: 'Lifecycle',
    hint: 'Plans use a calendar duration; a subscription customization may set an exact date.',
  },
  {
    key: MONTHLY_TRANSFER_POLICY_KEY,
    label: 'Monthly transfer',
    kind: 'monthly-transfer',
    default: {
      kind: 'monthly-transfer',
      allowanceBytes: null,
      softThresholdBytes: null,
      billingAnchorDay: 1,
      timeZone: 'UTC',
      graceHours: 24,
      enforcement: 'notify',
    },
    group: 'Traffic',
    hint: 'Ingress plus egress bytes per subscription-local billing period.',
  },
  {
    key: DISK_SOFT_POLICY_KEY,
    label: 'Disk soft threshold',
    kind: 'disk-soft-threshold',
    default: {
      kind: 'disk-soft-threshold',
      thresholdBytes: null,
      graceHours: 24,
      enforcement: 'notify',
    },
    group: 'Quotas',
    hint: 'An early warning threshold; the existing disk_mb limit remains the hard quota.',
  },
];

export function effectivePolicies(
  plan: PlanPolicies,
  overrides?: PolicyOverrides | null,
): PlanPolicies {
  return { ...plan, ...(overrides ?? {}) };
}

export function policyValue<T extends ServicePolicyValue['kind']>(
  policies: PlanPolicies,
  key: string,
  kind: T,
): Extract<ServicePolicyValue, { kind: T }> | null {
  const value = policies[key];
  return value?.kind === kind ? (value as Extract<ServicePolicyValue, { kind: T }>) : null;
}

export const acknowledgePolicyIncidentSchema = z.object({
  note: z.string().trim().min(1).max(2000),
});
export type AcknowledgePolicyIncidentInput = z.infer<typeof acknowledgePolicyIncidentSchema>;

export const extendSubscriptionExpirySchema = z
  .object({
    expiresOn: dateOnlySchema.optional(),
    additionalDays: z.number().int().min(1).max(3650).optional(),
    note: z.string().trim().min(1).max(2000),
    reactivate: z.boolean().default(true),
  })
  .refine((value) => (value.expiresOn ? 1 : 0) + (value.additionalDays ? 1 : 0) === 1, {
    message: 'Provide exactly one of expiresOn or additionalDays',
  });
export type ExtendSubscriptionExpiryInput = z.infer<typeof extendSubscriptionExpirySchema>;

export const reactivatePolicySubscriptionSchema = z.object({
  note: z.string().trim().min(1).max(2000),
});
export type ReactivatePolicySubscriptionInput = z.infer<
  typeof reactivatePolicySubscriptionSchema
>;

export const POLICY_INCIDENT_KINDS = [
  'disk_soft',
  'transfer_soft',
  'transfer_allowance',
  'expiry_warning',
  'expired',
] as const;
export type PolicyIncidentKind = (typeof POLICY_INCIDENT_KINDS)[number];

export const POLICY_INCIDENT_STATUSES = ['active', 'recovered'] as const;
export type PolicyIncidentStatus = (typeof POLICY_INCIDENT_STATUSES)[number];

export interface PolicyIncidentDto {
  id: string;
  subscriptionId: string;
  kind: PolicyIncidentKind;
  policyKey: string;
  status: PolicyIncidentStatus;
  periodStart: string | null;
  periodEnd: string | null;
  observedValue: number | null;
  thresholdValue: number | null;
  firstObservedAt: string;
  lastObservedAt: string;
  graceUntil: string | null;
  enforcement: PolicyEnforcementAction;
  enforcementAppliedAt: string | null;
  acknowledgedAt: string | null;
  acknowledgedBy: string | null;
  acknowledgementNote: string | null;
  recoveredAt: string | null;
}

export interface SubscriptionPolicySummaryDto {
  policies: PlanPolicies;
  incidents: PolicyIncidentDto[];
  deploymentBlocked: boolean;
  deploymentBlockedReason: string | null;
  policySuspended: boolean;
  policySuspensionReason: string | null;
}
